/**
 * Merges admin-authored content (fetched once at boot over 'tutorialContent')
 * onto the built-in, hardcoded curriculum in missions.ts:
 *  - `tutorial_overrides` patch a BUILT-IN lesson's layout/order/copy in place
 *    (its check predicate always stays the hand-written one in missions.ts).
 *  - `tutorial_lessons` are brand-new lessons, checked via a template (see
 *    checkTemplates.ts) instead of code, and slotted in by chapter + order.
 *
 * Every existing call site (App.ts, TutorialPanel.ts) keeps calling
 * `missionsFor`/`missionsForCourse`/`missionById` — just imported from here
 * instead of directly from missions.ts — so this is additive, not a rewrite.
 * Before any content arrives (or if the fetch never completes) these fall
 * back to exactly the built-in curriculum, unchanged.
 */
import type { TutorialLessonWire, TutorialOverrideWire } from '../net/protocol';
import { setDictEntry } from '../ui/i18n';
import { evalCheckTemplate } from './checkTemplates';
import {
  MISSIONS, CHAPTERS, COURSES, type Mission, type CourseId, type ChapterId,
} from './missions';

interface OrderedMission extends Mission {
  __order: number;
}

let effective: readonly Mission[] = MISSIONS;

function toCustomMission(w: TutorialLessonWire): OrderedMission {
  return {
    id: w.id,
    variant: w.variant,
    chapter: w.chapter as ChapterId,
    layout: w.layout,
    cueAngle: w.cueAngle ?? undefined,
    check: (ctx) => evalCheckTemplate(w.checkType, w.checkParams, ctx),
    retryLayout: w.retryLayout,
    desktopOnly: w.desktopOnly,
    hint: w.hint ?? undefined,
    __order: w.orderIndex,
  };
}

function withOverride(m: Mission, o: TutorialOverrideWire | undefined, idx: number): OrderedMission {
  if (!o) return { ...m, __order: idx };
  const merged: Mission = { ...m };
  if (o.layout) merged.layout = o.layout;
  if (o.cueAngle != null) merged.cueAngle = o.cueAngle;
  if (o.titleKo && o.titleEn) setDictEntry(`tut.${m.id}.title`, o.titleEn, o.titleKo);
  if (o.goalKo && o.goalEn) setDictEntry(`tut.${m.id}.goal`, o.goalEn, o.goalKo);
  if (o.tipKo && o.tipEn) setDictEntry(`tut.${m.id}.tip`, o.tipEn, o.tipKo);
  return { ...merged, __order: o.orderIndex ?? idx };
}

/** Apply freshly-fetched admin content. Safe to call again (e.g. after an edit). */
export function applyTutorialContent(data: { lessons: TutorialLessonWire[]; overrides: TutorialOverrideWire[] }): void {
  const overrideMap = new Map(data.overrides.map((o) => [o.id, o]));

  const base = MISSIONS.map((m, i) => withOverride(m, overrideMap.get(m.id), i));
  const custom = data.lessons.map((l) => {
    setDictEntry(`tut.${l.id}.title`, l.titleEn, l.titleKo);
    setDictEntry(`tut.${l.id}.goal`, l.goalEn, l.goalKo);
    setDictEntry(`tut.${l.id}.keys`, l.keysEn, l.keysKo);
    if (l.tipKo && l.tipEn) setDictEntry(`tut.${l.id}.tip`, l.tipEn, l.tipKo);
    setDictEntry(`tut.${l.id}.retry`, l.retryEn, l.retryKo);
    return toCustomMission(l);
  });

  const all = [...base, ...custom];
  all.sort((a, b) => {
    const ci = CHAPTERS.indexOf(a.chapter) - CHAPTERS.indexOf(b.chapter);
    return ci !== 0 ? ci : a.__order - b.__order;
  });
  effective = all;
}

/** The full merged curriculum, in play order. */
export function effectiveMissions(): readonly Mission[] {
  return effective;
}

/** The merged curriculum for this device — see missions.ts's `missionsFor`. */
export function missionsFor(touch: boolean): readonly Mission[] {
  return effective.filter((m) => (touch ? !m.desktopOnly : !m.touchOnly));
}

/** The merged curriculum for one course, on this device. */
export function missionsForCourse(course: CourseId, touch: boolean): readonly Mission[] {
  const chapters = COURSES.find((c) => c.id === course)!.chapters;
  return missionsFor(touch).filter((m) => chapters.includes(m.chapter));
}

/** Look a mission up by id, including admin-authored ones (null if unknown). */
export function missionById(id: string): Mission | null {
  return effective.find((m) => m.id === id) ?? null;
}
